// --------------------------------------------------------------
// Created On: 2022-06-23
// Author: Zachary Thomas
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment } from "react";
import DataHistoryControls from "../../../components/DataHistoryControls/DataHistoryControls";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import { toggleAssetGraphAttribute } from "src/redux/features/assetGraphAttributesSlice";
import useReduxSelector from "../../../hooks/useReduxSelector";
import styles from "./GraphControls.module.scss";

// Controls for what to display on asset data graph.
export default function GraphControls(props: Props): Component {
  const deviceAttributes = useReduxSelector((state) => state.assetGraphAttributes);
  const accumulationDeviceAttributes = useReduxSelector((state) =>
    state.assetGraphAttributes.filter((assetGraphAttribute) => assetGraphAttribute.isAccumulative)
  );
  const inAccumulationMode = [
    "HOURLY_ACCUMULATION",
    "DAILY_ACCUMULATION",
    "WEEKLY_ACCUMULATION",
    "MONTHLY_ACCUMULATION",
  ].includes(props.displayMode);
  const dispatch = useReduxDispatch();

  return (
    <Fragment>
      {props.loading ? null : (
        <div className={styles.body}>
          {deviceAttributes.length > 0 && (
            <DataHistoryControls
              startDate={props.startDate}
              endDate={props.endDate}
              displayMode={props.displayMode}
              axisMode={props.axisMode}
              allowLogs={false}
              allowAccumulation={true}
              allowStandardLine={true}
              allowMovingAverageLine={true}
              allowAxisControls={true}
              onChangeStartDate={(startDate) => props.onChangeStartDate(startDate)}
              onChangeEndDate={(endDate) => props.onChangeEndDate(endDate)}
              onChangeDisplayMode={(displayMode) => props.onChangeDisplayMode(displayMode)}
              onChangeAxisMode={(axisMode) => props.onChangeAxisMode(axisMode)}
            />
          )}

          <div className="row align-items-center m-0 py-5">
            {(inAccumulationMode && accumulationDeviceAttributes.length > 0) ||
            (!inAccumulationMode && deviceAttributes.length > 0) ? (
              <Fragment>
                {inAccumulationMode ? (
                  <Fragment>
                    {/* If we are in an accumulation mode, then we want to limit the possible attributes to just ones that support accumulation. */}
                    {accumulationDeviceAttributes.map((deviceAttribute) => (
                      <div key={deviceAttribute.code} className="col-12 col-lg-4 py-2">
                        <label
                          className={styles.label}
                          onClick={() => dispatch(toggleAssetGraphAttribute(deviceAttribute.code))}
                        >
                          {deviceAttribute.name}
                        </label>
                        <input
                          className={`form-check-input ${styles.check}`}
                          type="checkbox"
                          checked={deviceAttribute.checked}
                          onChange={() => dispatch(toggleAssetGraphAttribute(deviceAttribute.code))}
                        />
                      </div>
                    ))}
                  </Fragment>
                ) : (
                  <Fragment>
                    {/* If we are not in an accumulation mode, then we do not have to filter the list of attributes further */}
                    {deviceAttributes.map((deviceAttribute) => (
                      <div key={deviceAttribute.code} className="col-12 col-lg-4 py-2">
                        <label
                          className={styles.label}
                          onClick={() => dispatch(toggleAssetGraphAttribute(deviceAttribute.code))}
                        >
                          {deviceAttribute.name}
                        </label>
                        <input
                          className={`form-check-input ${styles.check}`}
                          type="checkbox"
                          checked={deviceAttribute.checked}
                          onChange={() => dispatch(toggleAssetGraphAttribute(deviceAttribute.code))}
                        />
                      </div>
                    ))}
                  </Fragment>
                )}
              </Fragment>
            ) : (
              <span className={styles.error}>
                The current monitoring device does not support any graphable attributes.
              </span>
            )}
          </div>

          {deviceAttributes.length > 0 && (
            <div className={styles.buttonContainer}>
              <button className="btn btn-primary" type="button" onClick={() => props.onGraph()}>
                Query Data
              </button>
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
}

interface Props {
  loading: boolean;
  startDate: string;
  endDate: string;
  displayMode: DisplayMode;
  axisMode: AxisMode;
  onChangeStartDate: (startDate: string) => void;
  onChangeEndDate: (endDate: string) => void;
  onChangeDisplayMode: (displayMode: DisplayMode) => void;
  onChangeAxisMode: (axisMode: AxisMode) => void;
  onGraph: () => void;
}

type DisplayMode =
  | "STANDARD_LINE_CHART"
  | "MOVING_LINE_CHART"
  | "HOURLY_ACCUMULATION"
  | "DAILY_ACCUMULATION"
  | "WEEKLY_ACCUMULATION"
  | "MONTHLY_ACCUMULATION"
  | "CHANGE_LOGS"
  | "ALL_LOGS";

type AxisMode = "Y_AXIS_ATTRIBUTE" | "Y_AXIS_UOM";
